import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../config/api";
import EmptyState from "../../../components/EmptyState";
import Loader from "../../../components/Loader";
import PageHeader from "../../../components/PageHeader";
import Pagination from "../../../components/Pagination";
import { FinanceBadge, formatMoney } from "../financeUtils";

const PAGE_SIZE = 10;
const STATUSES = ["All", "Pending", "Approved", "Rejected", "Paid"];
const EMPTY_FORM = {
  requestNumber: "",
  title: "",
  requestType: "Admin",
  department: "",
  amount: "",
  vendorId: "",
  projectId: "",
  status: "Pending",
  notes: "",
};

export default function AdminRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("All");
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const loadLookups = useCallback(async () => {
    try {
      const [vendorsRes, projectsRes] = await Promise.all([
        apiFetch("/api/finance/vendors?status=Active"),
        apiFetch("/api/projects"),
      ]);
      if (vendorsRes.ok) setVendors(await vendorsRes.json());
      if (projectsRes.ok) setProjects(await projectsRes.json());
    } catch {
      setVendors([]);
      setProjects([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (status !== "All") params.set("status", status);
      const res = await apiFetch(`/api/finance/admin-requests?${params.toString()}`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || "Could not load admin approvals.");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { loadLookups(); }, [loadLookups]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [status]);

  const totalPages = Math.max(1, Math.ceil(requests.length / PAGE_SIZE));
  const pagedRequests = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    return requests.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  }, [requests, page, totalPages]);

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function openCreate() {
    setForm(EMPTY_FORM);
    setModalOpen(true);
  }

  async function createRequest(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const res = await apiFetch("/api/finance/admin-requests", {
        method: "POST",
        body: JSON.stringify({
          ...form,
          amount: Number(form.amount || 0),
          vendorId: form.vendorId || null,
          projectId: form.projectId || null,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      setModalOpen(false);
      await load();
    } catch (err) {
      setError(err?.message || "Could not create admin request.");
    } finally {
      setSaving(false);
    }
  }

  async function updateStatus(requestId, nextStatus) {
    setSaving(true);
    setError("");
    try {
      const res = await apiFetch(`/api/finance/admin-requests/${requestId}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: nextStatus }),
      });
      if (!res.ok) throw new Error(await res.text());
      await load();
    } catch (err) {
      setError(err?.message || "Could not update admin request.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="finance-page">
      <PageHeader
        title="Admin Approvals"
        subtitle="Purchase, asset, vendor, and admin approvals for the workspace."
        actions={<button className="primary" onClick={openCreate}>New Request</button>}
      />

      <div className="glass-card" style={{ marginBottom: 16, padding: 14 }}>
        <div className="crm-stage-filter" aria-label="Approval status" style={{ justifyContent: "flex-start" }}>
          {STATUSES.map((item) => (
            <button key={item} type="button" className={status === item ? "active" : ""} onClick={() => setStatus(item)}>{item}</button>
          ))}
        </div>
      </div>

      {error && <div className="error-banner" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="glass-card finance-table-card">
        {loading ? (
          <div style={{ padding: 20 }}>{[1, 2, 3, 4].map((item) => <Loader key={item} type="skeleton" height={48} style={{ marginBottom: 10 }} />)}</div>
        ) : requests.length === 0 ? (
          <EmptyState title="No admin requests" subtitle="Create requests for purchases, assets, facilities, or vendor approvals." action={<button className="primary" onClick={openCreate}>New Request</button>} />
        ) : (
          <>
            <div className="finance-table-wrap">
              <table className="finance-table">
                <thead>
                  <tr>
                    <th>Request</th>
                    <th>Type</th>
                    <th>Department</th>
                    <th>Vendor</th>
                    <th>Requested By</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRequests.map((request) => (
                    <tr key={request.id}>
                      <td>
                        <div className="finance-row-title">{request.title}</div>
                        <div className="finance-row-subtitle">{request.requestNumber}</div>
                      </td>
                      <td>{request.requestType}</td>
                      <td>{request.department || "-"}</td>
                      <td>{request.vendorName || "-"}</td>
                      <td>{request.requestedByUserName || "-"}</td>
                      <td><strong>{formatMoney(request.amount)}</strong></td>
                      <td><FinanceBadge status={request.status} /></td>
                      <td>
                        {request.status === "Pending" ? (
                          <div className="finance-action-row">
                            <button className="btn-success" onClick={() => updateStatus(request.id, "Approved")} disabled={saving}>Approve</button>
                            <button className="btn-danger" onClick={() => updateStatus(request.id, "Rejected")} disabled={saving}>Reject</button>
                          </div>
                        ) : request.status === "Approved" ? (
                          <button className="primary" onClick={() => updateStatus(request.id, "Paid")} disabled={saving}>Mark Paid</button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={Math.min(page, totalPages)} totalPages={totalPages} onPageChange={setPage} total={requests.length} pageSize={PAGE_SIZE} />
          </>
        )}
      </div>

      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <form className="glass-card modal crm-modal" onSubmit={createRequest} onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3>New Admin Request</h3>
              <button type="button" className="modal-close" onClick={() => setModalOpen(false)} aria-label="Close">x</button>
            </div>
            <div className="crm-form-grid">
              <label>
                Request Number
                <input value={form.requestNumber} onChange={(event) => updateField("requestNumber", event.target.value)} placeholder="Auto if blank" />
              </label>
              <label>
                Title
                <input value={form.title} onChange={(event) => updateField("title", event.target.value)} required />
              </label>
              <label>
                Type
                <input value={form.requestType} onChange={(event) => updateField("requestType", event.target.value)} />
              </label>
              <label>
                Department
                <input value={form.department} onChange={(event) => updateField("department", event.target.value)} />
              </label>
              <label>
                Amount
                <input type="number" min="0" value={form.amount} onChange={(event) => updateField("amount", event.target.value)} />
              </label>
              <label>
                Vendor
                <select value={form.vendorId} onChange={(event) => updateField("vendorId", event.target.value)}>
                  <option value="">No vendor</option>
                  {vendors.map((vendor) => <option key={vendor.id} value={vendor.id}>{vendor.name}</option>)}
                </select>
              </label>
              <label>
                Project
                <select value={form.projectId} onChange={(event) => updateField("projectId", event.target.value)}>
                  <option value="">No project</option>
                  {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
                </select>
              </label>
              <label>
                Status
                <select value={form.status} onChange={(event) => updateField("status", event.target.value)}>
                  {STATUSES.filter((item) => item !== "All").map((item) => <option key={item}>{item}</option>)}
                </select>
              </label>
              <label className="crm-form-wide">
                Notes
                <textarea value={form.notes} onChange={(event) => updateField("notes", event.target.value)} rows={3} />
              </label>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-ghost" onClick={() => setModalOpen(false)}>Cancel</button>
              <button type="submit" className="primary" disabled={saving}>{saving ? "Saving..." : "Create Request"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
